import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { explicitNull, or } from 'airbnb-prop-types';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { getRouteUrl } from '@utils/urls';
import { numberToSup } from '@utils/formatNumber';
import { SQUARE } from '@utils/image';
import { measureProductCardView, measureProductClick } from '@utils/analytics';

import { SHOP_EVENT_AVAILABILITY_LABEL } from '@common/constants/shopEvent';
import { NAVBAR_HEIGHT_PX } from '@common/constants/ui';
import { SHOP_EVENT_PRODUCT } from '@common/routes';
import { PriceCrossed } from '@common/components/ProductComponents/ProductComponents.styled';
import PriceText from '@common/components/PriceText';
import LazyImg from '@common/components/LazyImg';

import {
  Title,
  ProductWrapper,
  ProductImageWrapper,
  ProductImageDiscount,
  Price,
  ProductAvailabilityLabel,
  ProductContent,
  ProductLink,
} from './ShopEventProductBox.styled';

const AVAILABILITY_LABEL_SHOW_ON_TYPES = [
  SHOP_EVENT_AVAILABILITY_LABEL.LOW,
  SHOP_EVENT_AVAILABILITY_LABEL.SOLDOUT,
];

const ShopEventProductBox = ({
  image,
  price,
  productColorId,
  slug,
  specialPrice,
  title,
  availabilityLabel,
  discountPercentage,
  colorTitle,
  availabilityLabelShouldAnimate,
  onAvailabilityLabelIntersection,
}) => {
  const router = useRouter();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const productRef = useRef();
  const wasMeasuredRef = useRef(false);

  const isSoldOut = availabilityLabel === SHOP_EVENT_AVAILABILITY_LABEL.SOLDOUT;
  const isSale = !!specialPrice;
  const [productId] = productColorId?.split('-') || [];
  const href = {
    pathname: SHOP_EVENT_PRODUCT,
    query: { id: productColorId, slug },
  };
  const hrefAs = getRouteUrl(language, SHOP_EVENT_PRODUCT, {
    id: productColorId,
    slug,
  });

  const handleProductLinkClick = () => {
    measureProductClick({
      color_id: productColorId,
      id: productId,
      name: title,
      price: specialPrice || price,
      productId,
      variant: colorTitle,
      specialPrice,
      quantity: 1,
      ordinaryPrice: price,
      affiliation: 'Online',
    });

    router.push(href, hrefAs);
  };

  useEffect(() => {
    const domElement = productRef.current;
    const onIntersectionChange = entries => {
      const entry = entries[0];

      if (entry.isIntersecting && !wasMeasuredRef.current) {
        wasMeasuredRef.current = true;

        measureProductCardView({
          color_id: productColorId,
          id: productId,
          name: title,
          price: specialPrice || price,
          productId,
          variant: colorTitle,
          specialPrice,
          quantity: 1,
          ordinaryPrice: price,
          affiliation: 'Online',
        });
      }
    };

    const observer = new IntersectionObserver(onIntersectionChange, {
      threshold: 0.6,
    });

    observer.observe(domElement);

    return () => {
      observer.unobserve(domElement);
    };
  }, []);

  const availabilityLabelRef = useRef();

  useEffect(() => {
    if (
      !availabilityLabelRef.current ||
      typeof onAvailabilityLabelIntersection !== 'function'
    ) {
      return undefined;
    }

    const handleIntersects = ([{ isIntersecting }]) => {
      onAvailabilityLabelIntersection(productColorId, isIntersecting);
    };

    const availabilityLabelObserver = new IntersectionObserver(
      handleIntersects,
      {
        threshold: 0.1,
        rootMargin: `-${NAVBAR_HEIGHT_PX}px 0px 0px`,
      },
    );

    availabilityLabelObserver.observe(availabilityLabelRef.current);

    return () => {
      availabilityLabelObserver.disconnect();
    };
  }, []);

  return (
    <ProductWrapper ref={productRef}>
      <Link
        legacyBehavior
        href={href}
        as={hrefAs}
        passHref
      >
        <ProductLink
          onClick={handleProductLinkClick}
          isSoldOut={isSoldOut}
        >
          <ProductImageWrapper>
            <LazyImg
              alt={title}
              src={image}
              widths={[491, 450, 380, 330, 440, 340, 270, 200, 375]}
              aspectRatios={SQUARE}
              shouldLazyLoad
            />
            {isSale && !!discountPercentage && (
              <ProductImageDiscount>{`− ${discountPercentage}%`}</ProductImageDiscount>
            )}
          </ProductImageWrapper>
          <ProductContent>
            <Title>{title}</Title>
            {isSale && (
              <PriceCrossed>
                <PriceText>{`CHF ${numberToSup(price)}`}</PriceText>
              </PriceCrossed>
            )}
            <Price isSale={isSale}>
              <PriceText>{`CHF ${numberToSup(isSale ? specialPrice : price)}`}</PriceText>
            </Price>
          </ProductContent>
        </ProductLink>
      </Link>
      {AVAILABILITY_LABEL_SHOW_ON_TYPES.includes(availabilityLabel) && (
        <ProductAvailabilityLabel
          ref={!isSoldOut ? availabilityLabelRef : null}
          $type={availabilityLabel}
          shouldAnimate={availabilityLabelShouldAnimate}
          suppressHydrationWarning
        >
          {isSoldOut
            ? t('shop.basket.soldOut.title', 'Ausverkauft')
            : t('shop.product.fewavailable.label', 'Wenig verfügbar')}
        </ProductAvailabilityLabel>
      )}
    </ProductWrapper>
  );
};

ShopEventProductBox.propTypes = {
  discountPercentage: or([PropTypes.number, explicitNull()]).isRequired,
  availabilityLabel: PropTypes.oneOf(
    Object.values(SHOP_EVENT_AVAILABILITY_LABEL),
  ).isRequired,
  image: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  productColorId: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  specialPrice: PropTypes.number,
  title: PropTypes.string.isRequired,
  colorTitle: PropTypes.string.isRequired,
  availabilityLabelShouldAnimate: PropTypes.bool,
  onAvailabilityLabelIntersection: PropTypes.func,
};

ShopEventProductBox.defaultProps = {
  specialPrice: 0,
  availabilityLabelShouldAnimate: false,
  onAvailabilityLabelIntersection: undefined,
};

export default ShopEventProductBox;
